import React, { FC, useCallback, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  DealerApplication,
  DealerArea,
  DealerAssistanceModel,
  DealerForm,
  DealerFullModel,
  SeatingPreference,
} from '../../../../shared/dealer';
import { OptionDataPayload } from '../../../../shared/options';
import { MaterialIcon, PageHeader, PermissionBoundary } from '../../../components';
import { JSONForm, PreSubmitData } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { Fetcher, renderName, useBoolState, useFetcher } from '../../../utils';
import { VendorSeatingRequests } from '../../apply/dealer/VendorSeating';
import { DealerApplicationForm } from './applicationDetails';
import { DealerEditForm } from './dealerEdit';

interface LoadingData {
  dealer: DealerFullModel;
  assistants: DealerAssistanceModel[];
  form: DealerForm;
  areas: DealerArea[];
  seating: SeatingPreference[];
}

export const DealerDetails: FC<RouteComponentProps<{ id: string }>> = ({ match }) => {
  const { ts } = useTranslation();
  const id = Number.parseInt(match.params.id, 10);

  const fetcher = useFetcher(async (): Promise<LoadingData> => {
    const [dealer, assistants, form, areas, seating] = await Promise.all([
      api.getDealer(id),
      api.getDealerAssistants(id),
      api.getDealerForm(),
      api.getDealerAreas(),
      api.getDealerSeatingPreferences(id),
    ]);

    return {
      assistants,
      dealer,
      form,
      areas,
      seating,
    };
  });

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  const { refresh } = fetcher;
  const { dealer, assistants, form, areas, seating } = fetcher.data;

  return (
    <>
      <PageHeader>{dealer.name}</PageHeader>
      <Row>
        <Col md={6} xs={12}>
          <Card className="mb-10">
            <CardBody>
              <DealerDetailsBody dealer={dealer} form={form} refresh={refresh} />
            </CardBody>
          </Card>
          <Card className="mb-10">
            <CardBody>
              <h3>{ts('assistants')}</h3>
              <div>
                <DealerAssistantsDetails
                  assistants={assistants}
                  dealer={dealer}
                  refresh={refresh}
                />
              </div>
            </CardBody>
          </Card>
          <VendorSeatingRequests dealer={dealer} reload={refresh} seatingPreferences={seating} />
        </Col>
        <Col className="mb-10" md={6} xs={12}>
          {dealer.applications.map((application) => {
            return (
              <Card className="mb-10" id={`application-${application.id}`} key={application.id}>
                <CardBody>
                  <DealerApplicationDetails
                    application={application}
                    area={application.dealerArea}
                    dealer={dealer}
                    refresh={refresh}
                  />
                </CardBody>
              </Card>
            );
          })}
          {areas
            .filter((t) => !dealer.applications.some((u) => u.areaId === t.id))
            .map((area) => {
              return (
                <DealerAreaCreate area={area} dealer={dealer} key={area.id} refresh={refresh} />
              );
            })}
        </Col>
      </Row>
    </>
  );
};

interface DealerDetailsBodyProps {
  readonly dealer: DealerFullModel;
  readonly form: DealerForm;
  readonly refresh: () => void;
}

const DealerDetailsBody: FC<DealerDetailsBodyProps> = ({ dealer, form, refresh }) => {
  const [edit, enableEdit, disableEdit] = useBoolState(false);
  const [options, setOptions] = useState<OptionDataPayload>(dealer.options);

  const { ts } = useTranslation();
  const onSuccess = useCallback(() => {
    toast.success(ts('the_dealer_has_been_updated'));
    refresh();
    disableEdit();
  }, [ts, refresh, disableEdit]);

  const startEdit = useCallback(() => {
    setOptions(dealer.options);
    enableEdit();
  }, [dealer.options, enableEdit]);

  const preSubmit = useCallback(
    (reqOpts: PreSubmitData<{ options: OptionDataPayload }>) => {
      reqOpts.inputs.options = options;
    },
    [options],
  );

  return (
    <Row>
      <Col xs={12}>
        <JSONForm<undefined, { options: OptionDataPayload }>
          method="post"
          onSuccess={onSuccess}
          path={`/api/users/${dealer.user.id}/dealer`}
          preSubmit={preSubmit}
        >
          <span style={{ float: 'right' }}>
            {!edit && (
              <ButtonGroup className="mb-10">
                <Button onClick={startEdit}>
                  <MaterialIcon id="editDealer" name="edit" />
                </Button>
              </ButtonGroup>
            )}
            {edit && (
              <ButtonGroup className="mb-10">
                <Button color="primary" id="submitDealer" type="submit">
                  {ts('common.save')}
                </Button>
                <Button onClick={disableEdit}>{ts('common.cancel')}</Button>
              </ButtonGroup>
            )}
          </span>
          <h3>{ts('dealer_information')}</h3>
          <DealerEditForm
            dealer={dealer}
            edit={edit}
            form={form}
            options={options}
            setOptions={setOptions}
          />
        </JSONForm>
      </Col>
    </Row>
  );
};

interface DealerAssistantsDetailsProps {
  readonly dealer: DealerFullModel;
  readonly assistants: DealerAssistanceModel[];
  readonly refresh: () => void;
}

const DealerAssistantsDetails: FC<DealerAssistantsDetailsProps> = ({
  dealer,
  assistants,
  refresh,
}) => {
  const { ts } = useTranslation();
  const assistantRemoved = useCallback(() => {
    refresh();
    toast.success(ts('assistant_removed'));
  }, [refresh, ts]);

  const assistantAdded = useCallback(() => {
    refresh();
    toast.success(ts('assistant_added'));
  }, [refresh, ts]);

  return (
    <>
      {assistants.length === 0 ? <p className="text-muted">{ts('no_assistants')}</p> : null}
      {assistants.map((assistant, index) => (
        <Col
          className="clearfix-after"
          id={`assistant${assistant.user.id}`}
          key={assistant.user.id}
          xs={12}
        >
          <div className="float-end">
            <PermissionBoundary inline requiredPermissions="vendor:update">
              <JSONForm
                method="delete"
                onSuccess={assistantRemoved}
                path={`/api/dealers/${dealer.id}/assistants/${assistant.user.id}`}
              >
                <Button className="action-delete" color="danger" type="submit">
                  <MaterialIcon name="delete" />
                </Button>
              </JSONForm>
            </PermissionBoundary>
          </div>
          <h4>
            <Link to={`/housekeeping/attendees/user/${assistant.user.id}`}>
              {renderName(assistant.user)}
            </Link>
          </h4>
          <div>{ts(assistant.accepted ? 'accepted' : 'pending_email_confirmation')}</div>
          {assistants.length - 1 !== index && <hr />}
        </Col>
      ))}
      <PermissionBoundary inline requiredPermissions="vendor:update">
        <hr />
        <JSONForm
          method="post"
          onSuccess={assistantAdded}
          path={`/api/dealers/${dealer.id}/assistants`}
        >
          <FormGroup>
            <Label for="username">{ts('add_assistant')}</Label>
            <Input id="username" name="username" placeholder={ts('assistant_username')} />
            <FormText color="muted">{ts('your_assistant_can_find_their')}</FormText>
          </FormGroup>
          <FormGroup>
            <Button block color="primary" id="submitForm" type="submit">
              {ts('add_assistant')}
            </Button>
          </FormGroup>
        </JSONForm>
      </PermissionBoundary>
    </>
  );
};

interface DealerApplicationDetailsProps {
  readonly dealer: DealerFullModel;
  readonly application?: DealerApplication;
  readonly area: { id: number; categoryId: number; name: string };
  readonly refresh: () => void;
}

const DealerApplicationDetails: FC<DealerApplicationDetailsProps> = ({
  area,
  dealer,
  application,
  refresh,
}) => {
  const [edit, enableEdit, disableEdit] = useBoolState(false);
  const { ts } = useTranslation();

  const onSubmit = useCallback(() => {
    toast.success(ts('the_application_has_been_updated'));
    refresh();
    disableEdit();
  }, [ts, refresh, disableEdit]);

  const fetcher = useFetcher(async () => await api.getFullProductsByCategory(area.categoryId));

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <Row>
      <Col xs={12}>
        {!edit && (
          <span style={{ float: 'right' }}>
            <ButtonGroup className="mb-10">
              <Button onClick={enableEdit}>
                <MaterialIcon id={`editArea${area.id}`} name="edit" />
              </Button>
            </ButtonGroup>
          </span>
        )}
        <DealerApplicationForm
          application={application}
          area={area}
          cancel={disableEdit}
          dealer={dealer}
          edit={edit}
          onSuccess={onSubmit}
          tableTypes={fetcher.data}
        />
      </Col>
    </Row>
  );
};

interface DealerAreaCreateProps {
  readonly dealer: DealerFullModel;
  readonly area: DealerArea;
  readonly refresh: () => void;
}

const DealerAreaCreate: FC<DealerAreaCreateProps> = ({ area, dealer, refresh }) => {
  const [edit, enableEdit, disableEdit] = useBoolState(false);
  const { ts } = useTranslation();

  const onSubmit = useCallback(() => {
    toast.success(ts('the_application_has_been_created'));
    refresh();
    disableEdit();
  }, [ts, refresh, disableEdit]);

  const fetch = useFetcher(
    async () => (edit ? await api.getFullProductsByCategory(area.categoryId) : undefined),
    [area.categoryId, edit],
  );

  return (
    <Card className="mb-10" id={`area-${area.id}`}>
      <CardBody>
        <Row>
          <Col xs={12}>
            <span style={{ float: 'right' }}>
              {!edit && (
                <ButtonGroup className="mb-10">
                  <Button onClick={enableEdit}>
                    <MaterialIcon id={`createArea${area.id}`} name="add" />
                  </Button>
                </ButtonGroup>
              )}
            </span>
            {edit ? (
              fetch.data ? (
                <DealerApplicationForm
                  area={area}
                  cancel={disableEdit}
                  dealer={dealer}
                  edit
                  onSuccess={onSubmit}
                  tableTypes={fetch.data}
                />
              ) : (
                <Fetcher result={fetch} />
              )
            ) : (
              <>
                <h3>{ts('areaname_application', area.name)}</h3>
                {ts('no_application')}
              </>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};
